import { instanziierenErlaubt } from '@asas/domain/project-status';
import type { ProjectStatus } from '@asas/domain/project-fields';
import type { WorkPackageDuplikat } from '@asas/domain';

import type { FehlerBefund } from '../daten/fehler';

/**
 * Reine Hilfen für die Arbeitspakete eines Projekts (AC-303).
 *
 * Entschieden wird am Server: Duplikate (E3), Leistungsphasen gegen die
 * Gruppierung (J1), Projektstatus (E7). Die Oberfläche zeigt an, was der Server
 * sagt, und bietet nur an, was er zulassen würde.
 */

/** Phasen als „LPH 5–7“ (zusammenhängend) oder „LPH 1, 3“. */
export function phasenAnzeige(phasen: readonly number[]): string {
  if (phasen.length === 0) return '';
  const sortiert = [...phasen].sort((a, b) => a - b);
  const erste = sortiert[0] ?? 0;
  const letzte = sortiert[sortiert.length - 1] ?? 0;

  if (sortiert.length === 1) return `LPH ${String(erste)}`;
  if (letzte - erste === sortiert.length - 1) return `LPH ${String(erste)}–${String(letzte)}`;
  return `LPH ${sortiert.join(', ')}`;
}

/** Plan-Stunden zur Anzeige; Stunden bleiben Zeichenketten (Regel 10). */
export function planStundenAnzeige(planHours: string | null): string {
  return planHours === null ? 'noch nicht geplant' : `${planHours.replace('.', ',')} h`;
}

/**
 * Eingabe in der Maske → Transport: getrimmt, Komma zu Punkt, leer heißt
 * „noch nicht geplant“. Ob die Zahl gültig ist, prüft der Server.
 */
export function planStundenEingabe(roh: string): string | null {
  const wert = roh.trim().replace(',', '.');
  return wert === '' ? null : wert;
}

/**
 * Bietet die Ansicht Anlegen an? Nur mit `projects:manage` und nur in einem
 * Status, in dem der Server es zuließe (E7) — sonst schriebe der erste Klick
 * eine Audit-Zeile (abgeschlossen ohne Statusrecht) oder liefe sicher in einen
 * Konflikt (verloren, archiviert).
 */
export function darfArbeitspaketeAnlegen(
  status: ProjectStatus,
  rechte: { readonly darfSchreiben: boolean; readonly darfStatus: boolean },
): boolean {
  return rechte.darfSchreiben && instanziierenErlaubt(status, rechte.darfStatus);
}

export type ArbeitspaketAktion = 'uebernehmen' | 'anlegen' | 'planstunden';

const WARNUNG_UNGEWISS =
  ' Möglicherweise wurde bereits gespeichert – bitte neu laden, bevor Sie es erneut versuchen.';

/**
 * Meldung je Aktion. Die Oberfläche liest den Fehlercode nicht selbst
 * (apps/web/CLAUDE.md); bei Konflikt und Eingabefehler nennt die Meldung die
 * bei DIESER Aktion möglichen Gründe (Muster AC-301 B8).
 */
export function meldungFuerArbeitspaketaktion(
  befund: FehlerBefund,
  aktion: ArbeitspaketAktion,
): string {
  let meldung = befund.meldung;

  if (befund.art === 'konflikt') {
    meldung =
      aktion === 'planstunden'
        ? 'Das Arbeitspaket wurde inzwischen geändert; bitte neu laden.'
        : aktion === 'uebernehmen'
          ? 'Ein Stammarbeitspaket ist archiviert, oder der Projektstatus lässt keine neuen Arbeitspakete zu.'
          : 'Der Projektstatus lässt keine neuen Arbeitspakete zu.';
  } else if (befund.art === 'eingabe') {
    meldung =
      aktion === 'planstunden'
        ? 'Plan-Stunden als Zahl ab 0 mit höchstens zwei Nachkommastellen.'
        : aktion === 'uebernehmen'
          ? 'Mindestens ein Stammarbeitspaket passt nicht zur LPH-Gruppierung des Mandanten.'
          : 'Bitte Bezeichnung, Gewerk, LPH-Gruppe und Fachgruppe prüfen.';
  } else if (befund.art === 'nicht_gefunden') {
    meldung =
      'Projekt, Stammarbeitspaket, Gewerk oder Fachgruppe gibt es nicht mehr; bitte neu laden.';
  }

  return befund.moeglicherweiseAusgefuehrt ? `${meldung}${WARNUNG_UNGEWISS}` : meldung;
}

/** Der Hinweis zu schon übernommenen Einträgen (E3). Nichts wurde angelegt. */
export function duplikatHinweis(duplikate: readonly WorkPackageDuplikat[]): string {
  const namen = duplikate.map((d) => `„${d.name}“ (${String(d.vorhanden)}× vorhanden)`).join(', ');
  return `Bereits im Projekt: ${namen}. Es wurde nichts angelegt. Ein weiteres Exemplar nur mit Bestätigung.`;
}
